// Hide "Head Office" branch — global storefront embed (no container).
//
// Head Office is an internal warehouse, not a customer-facing branch, so it must
// not appear in the storefront's branch UIs:
//   • the branch-selector drawer  (#branch-selector-drawer / .branch-selector-item)
//   • the store-locator page       (.sl-card sidebar entries + their map markers)
//
// It also empties the cart when the shopper clears their branch (branch-specific
// pricing/stock), and corrects a geolocation auto-select that lands on Head Office.
//
// Pure client-side hack — NO storefront/platform code is changed. Runs globally
// (no target element); every operation degrades to a no-op if the markup differs,
// so init() is safe to call on any page.

import { readCookie } from '../shared/identity.js';
import { apiJson } from '../shared/api.js';

function norm(s) { return String(s == null ? '' : s).toLowerCase().replace(/[\s_\-]/g, ''); }
// Internal warehouse / DC "branches" hidden from every customer-facing UI (not
// real customer branches). Normalised keys (lowercased, spaces/underscores/
// hyphens stripped) — add more here to hide them everywhere at once.
var HIDDEN_BRANCHES = ['headoffice', 'hq', 'capedc'];
function isHeadOffice(s) { return HIDDEN_BRANCHES.indexOf(norm(s)) !== -1; }

// ── "Head Office" role keeps Head Office visible ────────────────────────────
// A logged-in user whose storefront role is "Head Office" MUST still see the
// Head Office branch everywhere; for everyone else it stays hidden. Role comes
// from /api/credit/status (roleKey) — the same source the credit guard uses —
// so we reuse its cached promise when present.
//   _hoRole: null = not resolved yet, true = Head-Office role, false = other.
var _hoRole = null;

function resolveHeadOfficeRole() {
	var p = null;
	try { p = window.__PF_CREDIT_STATUS_PROMISE__; } catch (e) { p = null; }
	if (!p) {
		var headers = {};
		var jwt = readCookie('commerce_features'); if (jwt) headers['X-Commerce-Features'] = jwt;
		var sess = readCookie('Session'); if (sess) headers['X-Session-Reference'] = sess;
		// apiJson auto-attaches the Bearer key + app base URL (was hand-rolled from
		// window.__PARTS_FINDER_APP_BASE_URL__ / __PARTS_FINDER_API_KEY__ in the
		// legacy script). Cache the promise under the same global the credit guard
		// uses so both share one /api/credit/status round-trip.
		p = apiJson('/api/credit/status', { headers: headers }, null);
		try { window.__PF_CREDIT_STATUS_PROMISE__ = p; } catch (e) { /* ignore */ }
	}
	return p.then(function (s) { return !!(s && String(s.roleKey || '').trim().toUpperCase() === 'HEADOFFICE'); })
		.catch(function () { return false; });
}

// Un-hide drawer items hidden while the role was unknown (used when the visitor
// turns out to have the Head-Office role).
function revealHiddenDrawerItems() {
	var items = document.querySelectorAll('.branch-selector-item[data-pf-ho-hidden="1"]');
	for (var i = 0; i < items.length; i++) {
		var li = items[i];
		li.style.display = li.getAttribute('data-pf-ho-disp') || '';
		li.removeAttribute('data-pf-ho-hidden');
		li.removeAttribute('data-pf-ho-disp');
	}
}

var _hoRolePromise = null;
function ensureHeadOfficeRole() {
	if (_hoRolePromise) return _hoRolePromise;
	_hoRolePromise = resolveHeadOfficeRole().then(function (v) {
		_hoRole = v;
		try {
			if (v) {
				// Head-Office role — reveal anything hidden while the role was unknown.
				revealHiddenDrawerItems();
			} else {
				// Confirmed non-Head-Office — make sure everything is hidden and run the
				// auto-select correction that was deferred while the role was unknown.
				scan();
				fixHeadOfficeSelection();
			}
		} catch (e) { /* ignore */ }
		return v;
	});
	return _hoRolePromise;
}

// Remove Head Office store-locator cards. Also invoked from the patched
// Initialize (below) so the card is gone BEFORE the locator reads `.sl-card`
// to build its markers — which suppresses the map pin as well as the sidebar.
function stripLocatorCards(root) {
	if (_hoRole === true) return; // Head-Office role keeps Head Office on the locator.
	var cards = (root || document).querySelectorAll('.sl-card');
	for (var i = 0; i < cards.length; i++) {
		var card = cards[i];
		var span = card.querySelector('.sl-card-name');
		var name = card.getAttribute('data-name') || (span && span.textContent) || '';
		if (isHeadOffice(name)) { try { card.parentNode.removeChild(card); } catch (e) { card.style.display = 'none'; } }
	}
}

// Hide Head Office branch-selector drawer items. Uses display:none (reversible)
// rather than removeChild, so a visitor who turns out to have the Head-Office
// role can have them revealed once the role resolves.
function stripDrawerItems(root) {
	if (_hoRole === true) return; // Head-Office role keeps Head Office in the drawer.
	var items = (root || document).querySelectorAll('.branch-selector-item');
	for (var i = 0; i < items.length; i++) {
		var li = items[i];
		var btn = li.querySelector('[data-branch-name]');
		var span = li.querySelector('.branch-selector-item-name');
		var name = (btn && btn.getAttribute('data-branch-name')) || (span && span.textContent) || li.textContent || '';
		if (isHeadOffice(name) && li.getAttribute('data-pf-ho-hidden') !== '1') {
			li.setAttribute('data-pf-ho-hidden', '1');
			li.setAttribute('data-pf-ho-disp', li.style.display || '');
			li.style.display = 'none';
		}
	}
}

function scan() { stripDrawerItems(); stripLocatorCards(); relabelLocatorButtons(); }

// ── Keep Head Office out of the geolocation auto-select ─────────────────────
// The storefront's branch selector auto-selects the CLOSEST branch (by
// geolocation) from /ajax/branches — and Head Office, a warehouse we hide from
// every customer-facing UI, is in that list, so a nearby customer gets
// auto-selected onto a branch they can't even see (with its stock rolled into
// the display, no switch option). Head Office can NEVER be chosen deliberately
// (it's stripped from the drawer), so ANY Head-Office selection is an auto-select
// we should correct: switch to the nearest branch that ISN'T Head Office.
var _hoFixInFlight = false;

function topLS() { try { return (window.top || window).localStorage; } catch (e) { return window.localStorage; } }
function selectedBranchName() { try { return topLS().getItem('SelectedBranchName') || ''; } catch (e) { return ''; } }

function haversineM(la1, lo1, la2, lo2) {
	var R = 6371000, toRad = function (d) { return d * Math.PI / 180; };
	var dLa = toRad(la2 - la1), dLo = toRad(lo2 - lo1);
	var a = Math.sin(dLa / 2) * Math.sin(dLa / 2) +
		Math.cos(toRad(la1)) * Math.cos(toRad(la2)) * Math.sin(dLo / 2) * Math.sin(dLo / 2);
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function branchCoords(b) {
	var la = parseFloat(String(b && b.Latitude || '').replace(',', '.'));
	var lo = parseFloat(String(b && b.Longitude || '').replace(',', '.'));
	return (isFinite(la) && isFinite(lo)) ? { lat: la, lng: lo } : null;
}
function geoFromSession() {
	try {
		var la = parseFloat(sessionStorage.getItem('BranchSelectorGeoLat'));
		var lo = parseFloat(sessionStorage.getItem('BranchSelectorGeoLng'));
		return (isFinite(la) && isFinite(lo)) ? { lat: la, lng: lo } : null;
	} catch (e) { return null; }
}

// Persist a branch selection the same way the storefront's selectBranch() does:
// cookie `SelectedBranchId` (server middleware reads it) + localStorage id/name.
function persistBranch(id, name) {
	try { var ls = topLS(); ls.setItem('SelectedBranchId', String(id)); ls.setItem('SelectedBranchName', name || ''); } catch (e) { /* ignore */ }
	try { document.cookie = 'SelectedBranchId=' + encodeURIComponent(String(id)) + '; path=/; max-age=' + (365 * 24 * 60 * 60) + '; SameSite=Lax'; } catch (e) { /* ignore */ }
}
function clearBranch() {
	try { var ls = topLS(); ls.removeItem('SelectedBranchId'); ls.removeItem('SelectedBranchName'); } catch (e) { /* ignore */ }
	try { document.cookie = 'SelectedBranchId=; path=/; max-age=0'; } catch (e) { /* ignore */ }
}

var HO_ATTEMPTS_KEY = '__pf_ho_fix_attempts';
function hoAttempts() { try { return parseInt(sessionStorage.getItem(HO_ATTEMPTS_KEY) || '0', 10) || 0; } catch (e) { return 0; } }

function fixHeadOfficeSelection() {
	// Only correct a Head-Office auto-selection for confirmed NON-Head-Office
	// users. While the role is unknown (null) we defer — and a Head-Office-role
	// user (false-check fails) may have deliberately selected Head Office, so we
	// never correct them off it. ensureHeadOfficeRole() re-runs this once the
	// role resolves to non-Head-Office.
	if (_hoRole !== false) return;
	if (_hoFixInFlight) return;
	var currentName = selectedBranchName();
	// No branch selected yet — do nothing, and crucially DON'T reset the safety
	// cap. This function runs once on load (often BEFORE the storefront has
	// auto-selected a branch) and again on `branchSelected`. Resetting the cap on
	// that empty early call meant the counter never accumulated, so a no-geo
	// Head-Office pick would clear+reload → storefront re-picks HO → clear+reload
	// → forever. Only a real non-HO selection (below) may reset the cap.
	if (!currentName) return;
	if (!isHeadOffice(currentName)) {
		// Healthy non-HO selection — reset the per-session safety cap.
		try { sessionStorage.removeItem(HO_ATTEMPTS_KEY); } catch (e) { /* ignore */ }
		return;
	}
	// Safety cap: never reload more than a few times per session.
	if (hoAttempts() >= 3) return;
	_hoFixInFlight = true;
	var geo = geoFromSession();
	fetch('/ajax/branches', { headers: { Accept: 'application/json' } })
		.then(function (r) { return r.ok ? r.json() : null; })
		.then(function (data) {
			var branches = (data && data.Branches) ? data.Branches : (Array.isArray(data) ? data : []);
			var best = null, bestD = Infinity;
			if (geo) {
				for (var i = 0; i < branches.length; i++) {
					var b = branches[i];
					if (!b || isHeadOffice(b.Name)) continue;
					var c = branchCoords(b);
					if (!c) continue;
					var d = haversineM(geo.lat, geo.lng, c.lat, c.lng);
					if (d < bestD) { bestD = d; best = b; }
				}
			}
			if (best && best.StoreBranchId != null) {
				// Switch to the nearest non-Head-Office branch, then reload so the
				// storefront re-renders branch-specific pricing/stock. On the next
				// load the selection is a real non-HO branch → the cap is reset and
				// we're done (one reload, no loop).
				persistBranch(best.StoreBranchId, best.Name || '');
				try { sessionStorage.setItem(HO_ATTEMPTS_KEY, String(hoAttempts() + 1)); } catch (e) { /* ignore */ }
				try { window.location.reload(); } catch (e) { _hoFixInFlight = false; }
			} else {
				// Can't determine a better branch (no geolocation this session, or no
				// candidate branch has coords). Leave the current selection ALONE —
				// clearing it just makes the storefront auto-pick Head Office again on
				// reload, which is exactly the loop we must avoid. Sitting on HO is far
				// better than reloading endlessly; the user can pick a store manually.
				_hoFixInFlight = false;
			}
		})
		.catch(function () { _hoFixInFlight = false; /* allow a later retry (e.g. next branchSelected) */ });
}

function wireHeadOfficeAutoSelectFix() {
	// Persisted HO selection (e.g. auto-selected on an earlier visit).
	fixHeadOfficeSelection();
	// Same-session auto-select: the selector fires `branchSelected` from
	// selectBranch(), so we catch a fresh HO auto-select without polling.
	try {
		var $ = window.jQuery || window.$;
		if ($ && typeof $ === 'function') $(document).on('branchSelected', function () { fixHeadOfficeSelection(); });
	} catch (e) { /* ignore */ }
}

// ── Empty the cart when the shopper clears their branch ─────────────────────
// Cart pricing/stock is branch-specific, so once the branch is cleared the cart
// is no longer valid. The storefront's branch selector fires `branchCleared`
// from clearBranch(); we respond by clearing the cart the SAME way the core
// /checkout/cart "Clear Cart" button does — SFUI.cart.confirmClearCart() →
// sdk.cart.clearCart() — so it runs through the storefront's own SDK request
// layer + store context (and refreshes the mini-cart on success), rather than
// a hand-rolled request. Falls back to the SDK's own endpoint
// (DELETE /ajax/clear-cart) only when the SDK isn't on the page; keepalive keeps
// that fallback alive across the branch selector's page reload on PDPs.
var _cartClearInFlight = false;
function emptyCartOnBranchClear() {
	if (_cartClearInFlight) return;
	_cartClearInFlight = true;
	var win; try { win = window.top || window; } catch (e) { win = window; }
	var refreshMiniCart = function () {
		try {
			if (win.Cart && win.Cart.MiniCart && typeof win.Cart.MiniCart.GetMiniCart === 'function') win.Cart.MiniCart.GetMiniCart();
			if (win.Cart && typeof win.Cart.UpdateCartCount === 'function') win.Cart.UpdateCartCount();
		} catch (e) { /* ignore */ }
	};
	// Preferred: call the EXACT function the core /checkout/cart "Clear Cart"
	// button runs — SFUI.cart.confirmClearCart() (see the storefront's
	// ClearCart.cshtml: onclick="SFUI.cart.confirmClearCart()"). It clears via
	// sdk.cart.clearCart, reloads the cart summary and toasts, all in the store's
	// own context. SFUI is a site-wide global (registered in ui/cart.js), so it's
	// available wherever the branch can be cleared, not just on the cart page.
	// It takes no callback, so refresh the mini-cart shortly after it fires.
	try {
		var sfui = win.SFUI || window.SFUI;
		if (sfui && sfui.cart && typeof sfui.cart.confirmClearCart === 'function') {
			sfui.cart.confirmClearCart();
			setTimeout(function () { _cartClearInFlight = false; refreshMiniCart(); }, 600);
			return;
		}
	} catch (e) { /* fall through to the SDK / endpoint fallbacks */ }
	// Fallback: call the SDK directly (what confirmClearCart does internally)
	// when SFUI isn't on the page.
	try {
		var sdk = win.sdk || window.sdk;
		if (sdk && sdk.cart && typeof sdk.cart.clearCart === 'function') {
			sdk.cart.clearCart({
				success: function () { _cartClearInFlight = false; refreshMiniCart(); },
				error: function () { _cartClearInFlight = false; }
			});
			return;
		}
	} catch (e) { /* fall through to the direct-endpoint fallback */ }
	// Last resort: hit the SDK's own endpoint directly when neither is present.
	try {
		fetch('/ajax/clear-cart', { method: 'DELETE', credentials: 'same-origin', keepalive: true, headers: { Accept: 'application/json' } })
			.then(refreshMiniCart)
			.catch(function () { /* ignore */ })
			.then(function () { _cartClearInFlight = false; });
	} catch (e) { _cartClearInFlight = false; }
}

function wireClearCartOnBranchClear() {
	try {
		var $ = window.jQuery || window.$;
		if ($ && typeof $ === 'function') {
			$(document).off('branchCleared.pfClearCart').on('branchCleared.pfClearCart', function () { emptyCartOnBranchClear(); });
		}
	} catch (e) { /* ignore */ }
}

// ── Store-locator button is "View Store", NOT "Select store" ─────────────────
// The locator button only PREVIEWS a store: the native .sl-select-btn handler
// (contenteddietor.storelocator.js selectStore()) pans the map to the branch and
// opens its info window. It must NOT change the shopper's selected branch —
// setting the branch here fired a `branchSelected` event, which the storefront
// reacts to by RELOADING the page (to apply branch pricing/stock). That reload
// on every tap is the unwanted refresh reported on the locator; you should be
// able to browse store locations without the page reloading. The shopping branch
// is still changed the normal way, via the header branch-selector drawer.
//
// So we (1) relabel the native "Select Store" button to "View Store", and
// (2) wire NO branch-setting onto it — the plain map-pan is all it does.
function relabelLocatorButtons(root) {
	var btns = (root || document).querySelectorAll('.sl-select-btn');
	for (var i = 0; i < btns.length; i++) {
		var b = btns[i];
		// Guard so we don't rewrite the text node on every MutationObserver sweep.
		if ((b.textContent || '').trim() !== 'View Store') b.textContent = 'View Store';
	}
}

// Patch the store-locator Initialize so Head Office cards are removed before it
// reads `.sl-card` to build markers (kills the sidebar entry AND the map pin).
function patchLocator() {
	try {
		var L = window.ContentEddietorStoreLocator;
		if (L && L.Actions && typeof L.Actions.Initialize === 'function' && !L.Actions.__hoPatched) {
			var orig = L.Actions.Initialize;
			L.Actions.Initialize = function () {
				try { stripLocatorCards(); } catch (e) { /* ignore */ }
				try { relabelLocatorButtons(); } catch (e) { /* ignore */ }
				return orig.apply(this, arguments);
			};
			L.Actions.__hoPatched = true;
			return true;
		}
	} catch (e) { /* ignore */ }
	return false;
}

// Coalesced sweep for dynamically-rendered items — the drawer re-renders each
// time it opens / on search, and content parts can render late.
var scheduled = false;
function schedule() {
	if (scheduled) return;
	scheduled = true;
	var run = function () { scheduled = false; try { scan(); } catch (e) { /* ignore */ } };
	if (window.requestAnimationFrame) window.requestAnimationFrame(run); else setTimeout(run, 16);
}

export function init() {
	// Patch the locator now, and keep trying briefly until the locator script
	// defines its object (covers either script include order).
	if (!patchLocator()) {
		var tries = 0;
		var t = setInterval(function () { if (patchLocator() || ++tries > 60) clearInterval(t); }, 50);
	}

	// Kick off the role lookup ASAP so the Head-Office-role decision is ready
	// before the drawer opens / the locator initialises.
	try { ensureHeadOfficeRole(); } catch (e) { /* ignore */ }

	// Immediate sweep (items may already be in the DOM), then wire everything up.
	try { scan(); } catch (e) { /* ignore */ }
	try { wireHeadOfficeAutoSelectFix(); } catch (e) { /* ignore */ }
	try { wireClearCartOnBranchClear(); } catch (e) { /* ignore */ }
	try {
		new MutationObserver(schedule).observe(document.documentElement || document.body, { childList: true, subtree: true });
	} catch (e) { /* ignore */ }
}

export var id = 'hide-head-office';
